import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  AnyObject,
  ErrorMessage,
  GeometryMultiPolygon,
  GoogleDrawingManager,
  GoogleDrawingManagerOptions,
  GoogleMap
} from '@mri/types';
import { googleMapLoader, googleMapStyleLight } from '@mri/constants';
import {
  circleToPolygon,
  createButtonMap,
  createInputMap,
  polygonsFromGeoJson,
  polygonsToGeoJson
} from '@mri/functions';
import { getDevicePosition } from '@mri/functions';
import { tap } from 'rxjs';

@Component({
  selector: 'app-field-map-draw',
  templateUrl: './field-map-draw.component.html',
  styleUrl: './field-map-draw.component.scss'
})
export class FieldMapDrawComponent implements OnInit {

  private _map!: GoogleMap;
  private _blockReceive: boolean = false;
  private _value: GeometryMultiPolygon;
  private _drawingManager!: GoogleDrawingManager;
  private _classSet: Set<string> = new Set<string>();
  private _polygons: Array<google.maps.Polygon> = [];
  private _selectedPolygon?: google.maps.Polygon;

  @Input() placeholder?: string;
  @Input() set value(value: GeometryMultiPolygon) {
    if(this._blockReceive) return;
    if (this._value === value) return;

    this._clearPolygons();
    this._value = value;

    this._polygons = polygonsFromGeoJson(value);
    if (!this._map) return;

    this._polygons.forEach(p => p.setMap(this._map));
    this._fitBoundsForPolygons();
    this._addListeners();
  }
  @Input() label: string;
  @Input() errors?: Array<ErrorMessage>;
  @Input() image?: string | ((value: string) => string);

  @Output() valueChange = new EventEmitter<GeometryMultiPolygon>;

  @ViewChild('mapContainer') mapContainer: ElementRef;
  @ViewChild('mapElement') mapElement: ElementRef;

  ngOnInit(): void {
    googleMapLoader
      .load()
      .then(google => this._mapInit(google));
  }


  private _mapInit(google: AnyObject): void {
    const elementMap = document.getElementById('map');
    if (!elementMap) return;

    const OverlayType = google.maps.drawing.OverlayType;
    const mapOptions = {
      center: {
        lat: 10.382557,
        lng: -75.506496
      },
      zoom: 16,
      zoomControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: googleMapStyleLight
    };

    this._map = new google.maps.Map(elementMap, mapOptions);

    const drawerOptions: GoogleDrawingManagerOptions = {
      //drawingMode: google.maps.drawing.OverlayType.,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_LEFT,
        drawingModes: [OverlayType.CIRCLE, OverlayType.POLYGON]
      },

      polygonOptions: {
        editable: true,
        draggable: true,
        clickable: true
      },
      circleOptions: {
        editable: true,
        draggable: true,
        clickable: true
      }
    };

    this._drawingManager = new google.maps.drawing.DrawingManager(drawerOptions);
    google.maps.event.addListener(this._drawingManager, 'overlaycomplete', (event: AnyObject) => {

      let newPolygon: google.maps.Polygon | null = null;

      if (event.type === OverlayType.POLYGON) {
        newPolygon = event.overlay;
        this._addPolygon(newPolygon as google.maps.Polygon);
      }

      if (event.type === OverlayType.CIRCLE) {
        newPolygon = circleToPolygon(event.overlay.center, event.overlay.radius);

        newPolygon.setMap(this._map);
        event.overlay.setMap(null);
        this._addPolygon(newPolygon);
      }

      if (!newPolygon) return;

      google.maps.event.addListener(newPolygon, 'click', () => {
        this._selectPolygon(newPolygon as google.maps.Polygon);
      });

      google.maps.event.addListener(newPolygon, 'rightclick', () => {
        this._removePolygon(newPolygon as google.maps.Polygon);
      });
    });

    this._drawingManager.setMap(this._map);

    createButtonMap({
      body: '<i class="fi fi-rr-trash-restore-alt"></i>',
      title: 'Borrar poligono',

      position: google.maps.ControlPosition.TOP_LEFT,
      action: () => this._selectedPolygon && this._removePolygon(this._selectedPolygon),
      map: this._map
    });

    createButtonMap({
      body: '<i class="fi fi-br-compress"></i>',
      title: 'Ampliar mapa',

      position: google.maps.ControlPosition.TOP_RIGHT,
      action: () => {
        const container = this.mapContainer.nativeElement;
        const element = this.mapElement.nativeElement;
        const height = element.clientHeight;

        if (container.classList.contains('maximizer')) {
          container.classList.remove('maximizer');
          container.style.height = '';
        } else {
          container.classList.add('maximizer');
          container.style.height = `${height}px`;
        }
      },
      map: this._map
    });

    const input = createInputMap({
      placeholder: 'Buscar',
      position: google.maps.ControlPosition.TOP_LEFT,
      map: this._map
    });

    const searchBox = new google.maps.places.SearchBox(input);
    this._map.addListener('bounds_changed', () => {
      searchBox.setBounds(this._map.getBounds() as google.maps.LatLngBounds);
    });
    searchBox.addListener('places_changed', () => {
      const places = searchBox.getPlaces();

      if (places.length == 0) {
        return;
      }

      // For each place, get the icon, name and location.
      const bounds = new google.maps.LatLngBounds();

      places.forEach((place: AnyObject) => {
        if (!place.geometry || !place.geometry.location) {
          console.log('Returned place contains no geometry');
          return;
        }

        if (place.geometry.viewport) {
          // Only geocodes have viewport.
          bounds.union(place.geometry.viewport);
        } else {
          bounds.extend(place.geometry.location);
        }
      });
      this._map.fitBounds(bounds);
    });

    this._polygons.forEach(p => p.setMap(this._map));
    this._fitBoundsForPolygons();
    this._addListeners();

    if (this._value) return;

    getDevicePosition()
      .pipe(
        tap(([lng, lat]) => this._map.setCenter({ lat, lng })),
        tap(() => this._map.setZoom(10))
      ).subscribe();
  }

  onFocus() {
    this._classSet.add('card-focus');
  }

  onBlur() {
    this._classSet.delete('card-focus');
  }

  private _fitBoundsForPolygons() {
    if (!this._map) return;
    const bounds = new google.maps.LatLngBounds();

    this._polygons.forEach(polygon => {
      const paths = polygon.getPaths();
      paths.forEach(path => {
        path.forEach(latLng => {
          bounds.extend(latLng);
        });
      });
    });

    this._map.fitBounds(bounds);
  }

  private _addListeners() {
    this._polygons.forEach(newPolygon => {
      google.maps.event.addListener(newPolygon, 'click', () => {
        this._selectPolygon(newPolygon as google.maps.Polygon);
      });

      google.maps.event.addListener(newPolygon, 'rightclick', () => {
        this._removePolygon(newPolygon as google.maps.Polygon);
      });
    });

  }

  private _clearPolygons() {
    let polygon: google.maps.Polygon | undefined;
    while ((polygon = this._polygons?.shift())) {
      polygon.setMap(null);
    }
  }

  private _addPolygon(polygon: google.maps.Polygon): void {
    this._polygons.push(polygon);
    this._deselectPolygon();
    this._emitValue();
  }

  private _removePolygon(polygon: google.maps.Polygon): void {
    const index = this._polygons.indexOf(polygon);

    if (index === -1) return;

    this._polygons.splice(index, 1);
    polygon.setMap(null);

    this._emitValue();
  }

  private _selectPolygon(polygon: google.maps.Polygon): void {
    this._deselectPolygon();

    this._selectedPolygon = polygon;

    polygon.setEditable(true);
    ['bounds_changed', 'dragstart', 'drag', 'dragend', 'mouseup'].forEach((eventName) => {
      polygon.addListener(eventName, () => {
        this._emitValue();
      });
    });
    polygon.setDraggable(true);
  }

  private _deselectPolygon(): void {
    this._selectedPolygon = undefined;
    this._polygons.forEach(p => {
      p.setEditable(false);
      p.setDraggable(false);
      ['bounds_changed', 'dragstart', 'drag', 'dragend', 'mouseup'].forEach((eventName) => {
        p.unbind(eventName);
      });
    });
  }

  private _emitValue(): void {
    this._blockReceive = true;
    const value = polygonsToGeoJson(this._polygons);

    this.valueChange.emit(value);

    setTimeout(() => this._blockReceive = false, 500);
  }
}
